import type { EnrichedCatalogResultQuery } from 'bundles/browse/components/CatalogResultUtils';
import { domainsThatDisplayMastertrackCarousels } from 'bundles/browse/components/Domain/constants/domainData';
import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import { CURATED_COLLECTION_PARAMETER } from 'bundles/browse/constants';

import _t from 'i18n!nls/browse';

export const capitalizeSkillName = (skillName: string): string => {
  const splitSkillName = skillName.split(' ');
  const capitalizedSkillNameArray = splitSkillName.map((word) => word.charAt(0).toUpperCase() + word.substring(1));

  return capitalizedSkillNameArray.join(' ');
};

export const getCollectionLabelWithDomainOrSubdomainName = (
  collectionObject: EnrichedCatalogResultQuery,
  subdomainOrDomain: { id: string; name: string }
) => {
  let overriddenSubdomainName;

  // We want to use Language as the subdomain name for the collection label if the subdomain is other-languages
  if (subdomainOrDomain.id === 'other-languages') {
    overriddenSubdomainName = 'Language';
  }

  const subdomainOrDomainNameToUse = overriddenSubdomainName || subdomainOrDomain.name;
  const collectionId = collectionObject?.id;

  // The collectionId is made up of three parameters i.e "BROWSE_V2_QUERY:categoryMultiTag:data-science,trendingByEnrollmentsNumericTag,Desc"
  const parameterUsedToCurateCollection = collectionId?.split(',')[1];

  switch (parameterUsedToCurateCollection) {
    case CURATED_COLLECTION_PARAMETER.TRENDING_COURSES:
      return _t('Trending #{subdomainOrDomainNameToUse} Courses', { subdomainOrDomainNameToUse });
    case CURATED_COLLECTION_PARAMETER.MOST_POPULAR_COURSES:
      return _t('Most Popular #{subdomainOrDomainNameToUse} Courses', { subdomainOrDomainNameToUse });
    case CURATED_COLLECTION_PARAMETER.TOP_RATED_COURSES:
      return _t('Top Rated #{subdomainOrDomainNameToUse} Courses', { subdomainOrDomainNameToUse });
    case CURATED_COLLECTION_PARAMETER.MOST_POPULAR_CERTIFICATES:
      return _t('Most Popular Certificates in #{subdomainOrDomainNameToUse}', { subdomainOrDomainNameToUse });
    default:
      return collectionObject?.label;
  }
};

export const getDiscoveryCollectionLabelWithDomainOrSubdomainName = (
  collectionObject: DiscoveryCollection,
  subdomainOrDomain: { id: string; name: string }
) => {
  let overriddenSubdomainName;

  // We want to use Language as the subdomain name for the collection label if the subdomain is other-languages
  if (subdomainOrDomain.id === 'other-languages') {
    overriddenSubdomainName = 'Language';
  }

  const subdomainOrDomainNameToUse = overriddenSubdomainName || subdomainOrDomain.name;
  const collectionId = collectionObject?.id;

  // The collectionId is made up of two parameters i.e "BROWSE_V2_QUERY:mostPopularByEnrollmentsNumericTag"
  const parameterUsedToCurateCollection = collectionId?.split(':')?.[1];

  switch (parameterUsedToCurateCollection) {
    case CURATED_COLLECTION_PARAMETER.TRENDING_COURSES:
      return _t('Trending #{subdomainOrDomainNameToUse} Courses', { subdomainOrDomainNameToUse });
    case CURATED_COLLECTION_PARAMETER.MOST_POPULAR_COURSES:
      return _t('Most Popular #{subdomainOrDomainNameToUse} Courses', { subdomainOrDomainNameToUse });
    case CURATED_COLLECTION_PARAMETER.TOP_RATED_COURSES:
      return _t('Top Rated #{subdomainOrDomainNameToUse} Courses', { subdomainOrDomainNameToUse });
    case CURATED_COLLECTION_PARAMETER.MOST_POPULAR_CERTIFICATES:
      return _t('Most Popular Certificates in #{subdomainOrDomainNameToUse}', { subdomainOrDomainNameToUse });
    default:
      return collectionObject?.label;
  }
};

export const shouldDomainDisplayMastertrackCollection = (domainId?: string): boolean => {
  return !!domainId && domainsThatDisplayMastertrackCarousels.includes(domainId);
};
